import { Author } from 'codegen/schema'
import { PortableText, urlForImage } from 'lib/sanity'
import Image from 'next/image'
import Link from 'next/link'
import { formatDate } from 'utils/format-date'

import { ImageWrapper, LinkWrapper, Name, NameWrapper, Top, Wrapper } from './AuthorDetails.styles'

export const AuthorDetails = (props: {
  author?: Author
  forArticleCard?: boolean
  publishedDate?: number | string
}) => {
  const { author, forArticleCard, publishedDate } = props

  if (!author) return null

  const imageSize = forArticleCard ? 100 : 100

  return (
    <>
      <Wrapper forArticleCard={forArticleCard}>
        <Top forArticleCard={forArticleCard}>
          {author?.image && !forArticleCard && (
            <ImageWrapper>
              <Image
                src={urlForImage(author.image).height(imageSize).width(imageSize).fit('crop').url() || ''}
                width={imageSize}
                height={imageSize}
                alt={`Photo of the author ${author?.name}`}
                title={`Photo of the author ${author?.name}`}
              />
            </ImageWrapper>
          )}
          <div>
            <NameWrapper forArticleCard={forArticleCard}>
              {forArticleCard && <>By&nbsp;</>}
              <Name forArticleCard={forArticleCard}>{author?.name}</Name>
              {forArticleCard && publishedDate && <span>&nbsp;&nbsp;-&nbsp;&nbsp;{formatDate(publishedDate)}</span>}
            </NameWrapper>
            {!forArticleCard && <div>{<PortableText value={author?.bio || []} />}</div>}
            {(author.facebook || author.twitter || author.linkedIn || author.gitHub) && (
              <LinkWrapper>
                {author.facebook && (
                  <Link href={author.facebook} passHref legacyBehavior prefetch={false}>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 320 512"
                      className="u-vertAlign-mid u-margin-leftRight-small"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"></path>
                    </svg>
                  </Link>
                )}
                {author?.twitter && (
                  <Link href={author.twitter || 'https://twitter.com/'} passHref legacyBehavior prefetch={false}>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 512 512"
                      className="u-vertAlign-mid"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z"></path>
                    </svg>
                  </Link>
                )}

                {author?.gitHub && (
                  <Link href={author.gitHub || 'https://github.com/'} passHref legacyBehavior prefetch={false}>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      className="u-vertAlign-mid"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <title>GitHub</title>
                      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.44 9.8 8.2 11.38.6.12.82-.26.82-.58 0-.28-.01-1.02-.02-2-3.34.72-4.04-1.61-4.04-1.61-.55-1.4-1.34-1.78-1.34-1.78-1.09-.74.08-.72.08-.72 1.2.08 1.83 1.24 1.83 1.24 1.07 1.83 2.8 1.3 3.48.99.1-.78.42-1.3.76-1.6-2.67-.3-5.48-1.34-5.48-5.97 0-1.32.47-2.4 1.24-3.24-.12-.3-.54-1.52.12-3.16 0 0 1.02-.33 3.34 1.24a11.62 11.62 0 0 1 3.04-.4c1.03 0 2.06.14 3.04.4 2.32-1.57 3.34-1.24 3.34-1.24.66 1.64.24 2.86.12 3.16.78.84 1.24 1.92 1.24 3.24 0 4.64-2.8 5.66-5.48 5.97.42.36.82 1.1.82 2.22 0 1.6-.02 2.9-.02 3.3 0 .32.22.7.82.58C20.56 21.8 24 17.3 24 12c0-6.63-5.37-12-12-12z" />
                    </svg>
                  </Link>
                )}

                {author?.linkedIn && (
                  <Link href={author.linkedIn || 'https://linkedin.com/'} passHref legacyBehavior prefetch={false}>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 448 512"
                      className="u-vertAlign-mid"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-80.4-48.29-80.4-48.29 0-55.69 37.7-55.69 76.7V448h-92.89V148.9h92.89v40.8h1.3c12.4-23.5 42.69-48.29 87.88-48.29 94 0 111.28 61.9 111.28 142.3V448z"></path>
                    </svg>
                  </Link>
                )}
              </LinkWrapper>
            )}
          </div>
        </Top>
      </Wrapper>
    </>
  )
}
